import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';
import type { ApolloError } from 'apollo-client';

import Heading from '@atlaskit/heading';
import { xcss, Stack, Box } from '@atlaskit/primitives';

import { ObservableSpinner } from '@confluence/infinite-scroll';
import { ErrorDisplay } from '@confluence/error-boundary';

import { VersionHistoryItem } from './VersionHistoryItem';
import type { ContentVersionHistoryQuery_contentVersionHistory_nodes as VersionHistoryNodeType } from './graphql/__types__/ContentVersionHistoryQuery';
import { ErrorState } from './ErrorState';

const i18n = defineMessages({
	currentVersionTitle: {
		id: 'version-history.current.version.title',
		defaultMessage: 'Current version',
		description: 'Title for current version section',
	},
	autoSavedVersionsTitle: {
		id: 'version-history.auto-saved.version.title',
		defaultMessage: 'Autosaved versions',
		description: 'Title for current version section',
	},
});

const bodyContentWrapperStyles = xcss({
	overflowY: 'auto',
	flexGrow: 1,
});

const SectionTitle = ({ title }: { title: MessageDescriptor }) => (
	<Box paddingBlock="space.100" paddingInlineStart="space.300" paddingInlineEnd="space.150">
		<Heading size="xsmall">
			<FormattedMessage {...title} />
		</Heading>
	</Box>
);

type VersionHistoryContentTypes = {
	currentVersion: VersionHistoryNodeType | null;
	error: ApolloError | undefined;
	autosavedVersions: VersionHistoryNodeType[];
	hasNextPage: boolean;
	loadMoreVersions: () => void;
	cursor: string | null;
	refetch: () => void;
	loading: boolean;
};

export const VersionHistoryContent = ({
	currentVersion,
	error,
	autosavedVersions,
	hasNextPage,
	loadMoreVersions,
	cursor,
	refetch,
	loading,
}: VersionHistoryContentTypes) => {
	if (error && !loading) {
		return (
			<ErrorDisplay error={error}>
				<ErrorState refetch={refetch} />
			</ErrorDisplay>
		);
	}

	return (
		<Box xcss={bodyContentWrapperStyles}>
			{currentVersion && (
				<Stack>
					<SectionTitle title={i18n.currentVersionTitle} />
					<VersionHistoryItem version={currentVersion} />
				</Stack>
			)}
			{autosavedVersions.length > 0 && (
				<Stack>
					<SectionTitle title={i18n.autoSavedVersionsTitle} />
					{autosavedVersions.map((version: VersionHistoryNodeType) => {
						return <VersionHistoryItem version={version} key={version.number} />;
					})}
					{hasNextPage && (
						<ObservableSpinner
							cursor={cursor}
							testId="version-history-loading-spinner"
							onScrollTo={loadMoreVersions}
							observerOptions={{
								root: null,
								rootMargin: '0px',
								threshold: 0.05,
							}}
						/>
					)}
				</Stack>
			)}
		</Box>
	);
};
